import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isTaskOverdue, makeTaskComment } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { CustomFieldInputs } from '../../components/CustomFieldInputs';
import { listClientDocs, uploadClientDoc, resolveDocSrc } from '../../storage';


function AddTaskModal({ data, onClose }) {
  const { clients, employees, tasks, setTasks, user, showToast, runSubmitting, org } = useApp();
  const [form, setForm] = useState({
    title:      "",
    clientId:   data?.clientId || "",
    assignedTo: data?.assignedTo || employees[0]?.id || "",
    dueDate:    "",
    notes:      "",
    category:   "General",
    requirementType: "none",
    customData: {},
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title)    { showToast("Task title required", "error"); return; }
    if (!form.clientId) { showToast("Select a client", "error"); return; }

    await runSubmitting('Adding task…', async () => {
    const client  = clients.find(c => c.id === form.clientId);
    const newTask = {
      id:              uuid(),
      title:           form.title,
      clientId:        form.clientId,
      clientName:      client?.name || "",
      invoiceId:       null,
      assignedTo:      form.assignedTo || null,
      status:          "open",
      sequence:        (tasks.filter(t => t.clientId === form.clientId).length) + 1,
      requirementType: form.requirementType,
      docTemplateId:   null,
      dueDate:         form.dueDate || null,
      completedDate:   null,
      notes:           form.notes,
      category:        form.category,
      isRecurring:     false,
    };

    const initialComments = form.notes?.trim()
      ? [makeTaskComment(user, form.notes)].filter(Boolean)
      : [];

    // Save to DB
    const result = await sb.insert("tasks", {
      id:               newTask.id,
      title:            newTask.title,
      company_id:       newTask.clientId,
      client_name:      newTask.clientName,
      invoice_id:       null,
      assigned_to:      newTask.assignedTo,
      status:           "open",
      sequence:         newTask.sequence,
      requirement_type: newTask.requirementType,
      due_date:         newTask.dueDate || null,
      notes:            "",
      comments:         initialComments,
      category:         newTask.category,
      custom_data:      form.customData || {},
    });

    if (result.length > 0) {
      setTasks(ts => [...ts, { ...newTask, notes: "", comments: initialComments }]);
      if (newTask.assignedTo) {
        notify(newTask.assignedTo, {
          type: "task",
          title: "Task assigned to you",
          body: `${newTask.title} — ${client?.name || "client"}`,
          meta: { taskId: newTask.id, modal: "view-task" },
        });
      }
      showToast(`Task "${form.title}" added for ${client?.name}!`, "success");
      onClose();
    } else {
      showToast("Failed to save task", "error");
    }
    }, 'task');
  };

  return (
    <div className="modal-box" style={{ maxWidth: 520 }}>
      <div className="modal-head">
        <div className="modal-title">Add New Task</div>
        <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>
      <div className="modal-body">
        <div className="form-grid">
          <div className="f-group">
            <label className="f-label">Task Title <span className="f-req">*</span></label>
            <input className="f-input" value={form.title} onChange={e=>set("title",e.target.value)} placeholder="e.g. GST Return Filing — March 2025" />
          </div>
          <div className="f-group">
            <label className="f-label">Client <span className="f-req">*</span></label>
            <select className="f-select" value={form.clientId} onChange={e=>set("clientId",e.target.value)}>
              <option value="">Select client…</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Assign To</label>
            <select className="f-select" value={form.assignedTo} onChange={e=>set("assignedTo",e.target.value)}>
              <option value="">Unassigned</option>
              {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Due Date</label>
            <input className="f-input" type="date" value={form.dueDate} onChange={e=>set("dueDate",e.target.value)} />
            <div className="f-hint">Leave blank — employee can set later.</div>
          </div>
          <div className="f-group">
            <label className="f-label">Category</label>
            <select className="f-select" value={form.category} onChange={e=>set("category",e.target.value)}>
              {["General","GST","ROC","Income Tax","TDS","Audit","DSC","Trademark","MSME","Other"].map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Client Action Required?</label>
            <select className="f-select" value={form.requirementType} onChange={e=>set("requirementType",e.target.value)}>
              <option value="none">No — team handles internally</option>
              <option value="document">Yes — client needs to submit documents</option>
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Team Notes</label>
            <textarea className="f-textarea" rows={3} value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Visible to the client in My Tasks…" />
          </div>
          <CustomFieldInputs
            fields={org?.customFields?.tasks || []}
            values={form.customData}
            onChange={v => set("customData", v)}
          />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Add Task</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// EDIT TASK MODAL — Update task details, reassign, change due date
// ═══════════════════════════════════════════════════════════════════════

export { AddTaskModal };
